/**
 * Hex-territory "Map" card for `/explore` (Decisions 6-12,
 * `specs/pending/explore-map.md`, amended 2026-09-14 through three design
 * review passes to v3: packed tiles, no ground, no text on the map). Every
 * area's capabilities compact into one packed hex cluster (`hexLayout.ts`'s
 * compaction pass); the whole landmass is one contiguous shape with no
 * background gaps inside it — areas are told apart by geometry (cluster
 * boundaries) plus hover, not colour or text. The map is monochrome: every
 * tile is a shade of `palette.primary` by coverage (solid / mid-alpha+dot /
 * pale), a never-explored area's tiles are `palette.neutral` instead, and
 * hovering a tile or an area-key row swaps that area's tiles to
 * `palette.accent` shades — colour appears only on hover. A real area with
 * zero capabilities still gets one layout cell (Decision 6 v3) drawn as a
 * small `palette.neutral` empty tile, hoverable/clickable like a real one, so
 * the landmass has no hole there and its key row has a target. Leads are dashed
 * neutral "?" markers on the exterior cell `placeLead` finds; running
 * workers are pulsing numbered dots that glide as the overlay polls. Area
 * names/ages/tile-counts live in `ExplorationMapKey`, not on the map.
 *
 * Hover: one HTML card (styled like `packages/ui`'s `ChartTooltip`), driven
 * by a single `hover` state set from `onMouseMove`/`onMouseLeave` on the
 * `<svg>` — event delegation via `data-hover-kind`/`data-hover-id` on the
 * hovered shape, rather than a handler per shape. A native SVG `<title>`
 * would be simpler, but can't be styled or positioned. Navigation still goes
 * through TanStack `Link` (SPA navigation, not `<a href>`) — React renders
 * `Link`'s underlying `<a>` in the SVG namespace when nested inside an
 * `<svg>`, so it draws correctly there.
 *
 * @module features/exploration/components/ExplorationMap
 */

import { useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardContent, Heading, Text, EmptyState, LoadingOrbit, chartThemes, useTheme } from '@chatsdet/ui';
import { MapTrifold } from '@phosphor-icons/react';
import type { ExplorationMapOverlay } from '@chatsdet/types';
import { useProductMap } from '../../product-map/useProductMap';
import { COVERAGE_STATUS_LABEL } from '../../product-map/productMapLogic';
import type { ProductCapability } from '../../product-map/types';
import { useExplorationMap } from '../map/useExplorationMap';
import { buildHexLayout, cellKey, hexToPixel, placeLead, type HexCell, type HexLayout } from '../map/hexLayout';
import { ageWord, COVERAGE_ALPHA, hexPoints, type Point } from '../map/svgGeometry';
import { ExplorationMapLegend } from './ExplorationMapLegend';
import { ExplorationMapKey } from './ExplorationMapKey';

type Props = { propertyId: string };

type Lead = ExplorationMapOverlay['leads'][number];
type Worker = ExplorationMapOverlay['workers'][number];

type HoverState =
  | { kind: 'tile'; x: number; y: number; capability: ProductCapability; areaId: string | null; areaTitle: string | null; age: string | null }
  | { kind: 'lead'; x: number; y: number; lead: Lead }
  | { kind: 'worker'; x: number; y: number; worker: Worker; areaTitle: string | null }
  | { kind: 'area'; x: number; y: number; areaTitle: string };

/** Pixel geometry of one drawn hex. Denser than the pre-v3 map since tiles pack edge-to-edge with only a 2px background gap. */
const HEX_RADIUS = 30;
/** Tiles are inset from the hex grid by 1.5px so the card background shows through as a thin gap between packed neighbours. */
const TILE_RADIUS = HEX_RADIUS - 1.5;
const VIEWBOX_PADDING = 28;
const HOVER_CARD_WIDTH = 240;
const HOVER_CARD_HEIGHT = 130;

export function ExplorationMap({ propertyId }: Props) {
  const mapQ = useProductMap(propertyId);
  const overlayQ = useExplorationMap(propertyId);
  const { config } = useTheme();
  const palette = chartThemes[config.theme];
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [highlightAreaId, setHighlightAreaId] = useState<string | null>(null);

  const capabilitiesById = useMemo(() => {
    const byId = new Map<string, ProductCapability>();
    if (!mapQ.data) return byId;
    for (const area of mapQ.data.areas) for (const c of area.capabilities) byId.set(c.id, c);
    for (const c of mapQ.data.unassignedCapabilities) byId.set(c.id, c);
    return byId;
  }, [mapQ.data]);

  // Decision 17: the layout is memoized on the product-map query's data
  // identity only — the 5s overlay poll must never recompute it.
  const layout: HexLayout = useMemo(() => {
    if (!mapQ.data) return { areas: [], tiles: [] };
    const allCapabilities = [
      ...mapQ.data.areas.flatMap((area) => area.capabilities),
      ...mapQ.data.unassignedCapabilities,
    ];
    return buildHexLayout(mapQ.data.areas, allCapabilities);
  }, [mapQ.data]);

  const areaIdByCapability = useMemo(() => new Map(layout.tiles.map((t) => [t.capabilityId, t.areaId])), [layout]);
  const areaTitleById = useMemo(() => new Map(layout.areas.map((a) => [a.areaId, a.label])), [layout]);

  const overlay = overlayQ.data;

  const lastExploredByArea = useMemo(() => {
    const byId = new Map<string, string | null>();
    overlay?.areas.forEach((a) => byId.set(a.areaId, a.lastExploredAt));
    return byId;
  }, [overlay]);

  // Exploration's own reach layer — never `capability.blockedReason`
  // (coverage-blocked, human-set). Keyed by the same prefixed capability id
  // the overlay and the product-map query both use.
  const explorationOutcomeById = useMemo(() => {
    const byId = new Map<string, ExplorationMapOverlay['capabilityOutcomes'][number]>();
    overlay?.capabilityOutcomes.forEach((o) => byId.set(o.capabilityId, o));
    return byId;
  }, [overlay]);

  /** Fogged only once the overlay has actually resolved and says never-explored — never fog while it's still loading. */
  function isAreaFogged(areaId: string | null): boolean {
    if (areaId === null || overlay === undefined) return false;
    return lastExploredByArea.get(areaId) === null;
  }

  const leadPlacements = useMemo(() => {
    if (!overlay) return [];
    const taken = new Set<string>();
    const placed: { lead: Lead; cell: HexCell }[] = [];
    for (const lead of overlay.leads) {
      if (lead.areaId === null) continue;
      const cell = placeLead(layout, lead.areaId, taken);
      // `placeLead` searches out to hex distance 2, so this is `null` only
      // when no unclaimed hex exists at all within that radius — practically
      // never. Dropping the lead (not drawing it) is the accepted outcome.
      if (!cell) continue;
      taken.add(cellKey(cell));
      placed.push({ lead, cell });
    }
    return placed;
  }, [overlay, layout]);

  const areaCentroids = useMemo(() => {
    const byId = new Map<string, Point>();
    for (const areaEntry of layout.areas) {
      if (areaEntry.areaId === null || areaEntry.cells.length === 0) continue;
      const points = areaEntry.cells.map((cell) => hexToPixel(cell, HEX_RADIUS));
      byId.set(areaEntry.areaId, {
        x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
        y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
      });
    }
    return byId;
  }, [layout]);

  const workerPositions = useMemo(() => {
    if (!overlay) return [];
    const cellByCapability = new Map(layout.tiles.map((t) => [t.capabilityId, t.cell]));
    const positions: { worker: Worker; point: Point }[] = [];
    for (const worker of overlay.workers) {
      let point: Point | undefined;
      if (worker.capabilityId) {
        const cell = cellByCapability.get(worker.capabilityId);
        if (cell) point = hexToPixel(cell, HEX_RADIUS);
      }
      if (!point && worker.areaId) point = areaCentroids.get(worker.areaId);
      if (point) positions.push({ worker, point });
    }
    return positions;
  }, [overlay, layout, areaCentroids]);

  const bounds = useMemo(() => {
    const points: Point[] = [];
    for (const areaEntry of layout.areas) {
      for (const cell of areaEntry.cells) points.push(hexToPixel(cell, HEX_RADIUS));
    }
    if (points.length === 0) return null;
    const pad = HEX_RADIUS + VIEWBOX_PADDING;
    const minX = Math.min(...points.map((p) => p.x)) - pad;
    const maxX = Math.max(...points.map((p) => p.x)) + pad;
    const minY = Math.min(...points.map((p) => p.y)) - pad;
    const maxY = Math.max(...points.map((p) => p.y)) + pad;
    return { minX, minY, width: maxX - minX, height: maxY - minY };
  }, [layout]);

  const totalCapabilities = capabilitiesById.size;
  const areaCount = mapQ.data?.areas.length ?? 0;

  // Keyed on `layout` alone so a poll tick (which only changes
  // `lastExploredByArea`) never recomputes this — `keyRows` below only
  // merges age into it. Also drives the empty-area placeholder rendering:
  // an area with a layout cell but zero tiles is empty (Decision 6 v3).
  const tileCountByArea = useMemo(() => {
    const counts = new Map<string | null, number>();
    for (const tile of layout.tiles) counts.set(tile.areaId, (counts.get(tile.areaId) ?? 0) + 1);
    return counts;
  }, [layout]);

  const keyRows = useMemo(
    () =>
      layout.areas.map((a) => ({
        areaId: a.areaId,
        label: a.label,
        tileCount: tileCountByArea.get(a.areaId) ?? 0,
        age: ageWord(a.areaId ? (lastExploredByArea.get(a.areaId) ?? null) : null),
      })),
    [layout, tileCountByArea, lastExploredByArea]
  );

  function handleSvgHover(e: ReactMouseEvent<SVGSVGElement>) {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const target = (e.target as Element).closest('[data-hover-kind]');
    if (!target) {
      setHover(null);
      setHighlightAreaId(null);
      return;
    }
    const rect = wrapper.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width - HOVER_CARD_WIDTH));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height - HOVER_CARD_HEIGHT));
    const kind = target.getAttribute('data-hover-kind');
    const id = target.getAttribute('data-hover-id') ?? '';

    if (kind === 'tile') {
      const capability = capabilitiesById.get(id);
      if (!capability) {
        setHighlightAreaId(null);
        return setHover(null);
      }
      const areaId = areaIdByCapability.get(id) ?? null;
      setHighlightAreaId(areaId);
      setHover({
        kind: 'tile',
        x,
        y,
        capability,
        areaId,
        areaTitle: areaId ? (areaTitleById.get(areaId) ?? null) : null,
        age: areaId ? ageWord(lastExploredByArea.get(areaId) ?? null) : null,
      });
    } else if (kind === 'lead') {
      setHighlightAreaId(null);
      const lead = leadPlacements.find((p) => p.lead.id === id)?.lead;
      if (lead) setHover({ kind: 'lead', x, y, lead });
    } else if (kind === 'worker') {
      setHighlightAreaId(null);
      const wp = workerPositions.find((p) => p.worker.runId === id);
      if (wp) {
        setHover({
          kind: 'worker',
          x,
          y,
          worker: wp.worker,
          areaTitle: wp.worker.areaId ? (areaTitleById.get(wp.worker.areaId) ?? null) : null,
        });
      }
    } else if (kind === 'area') {
      setHighlightAreaId(id);
      const areaTitle = areaTitleById.get(id);
      if (areaTitle) setHover({ kind: 'area', x, y, areaTitle });
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <Heading level={3}>Map</Heading>
        <Text size="xs" className="font-mono text-muted-foreground">
          {totalCapabilities} capabilities · {areaCount} areas
          {overlay?.cycle ? ` · ${overlay.cycle.status} · ${overlay.workers.length} workers` : ''}
        </Text>
      </CardHeader>
      <CardContent>
        {overlayQ.isError && (
          <Text size="xs" className="mb-2 text-destructive">
            {overlayQ.error instanceof Error ? overlayQ.error.message : 'Live overlay unavailable'}
          </Text>
        )}
        {mapQ.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingOrbit size="lg" />
          </div>
        ) : totalCapabilities === 0 || !bounds ? (
          <EmptyState
            icon={<MapTrifold size={24} />}
            title="No product map yet"
            description="Map this application's areas and capabilities to see the exploration territory."
            action={
              <Link
                to="/product-map"
                search={{ propertyId, view: 'explore' }}
                className="text-sm text-primary hover:underline"
              >
                Open Product Map →
              </Link>
            }
          />
        ) : (
          <>
            <style>{`
              @keyframes xmap-worker-pulse { 0% { r: 9; opacity: 0.3; } 100% { r: 18; opacity: 0; } }
              @keyframes xmap-lead-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
              .xmap-worker-pulse { animation: xmap-worker-pulse 1.8s ease-out infinite; }
              .xmap-lead-pulse { animation: xmap-lead-pulse 3s ease-in-out infinite; }
              @media (prefers-reduced-motion: reduce) {
                .xmap-worker-pulse, .xmap-lead-pulse { animation: none; }
              }
            `}</style>
            <div className="flex flex-col gap-4 md:flex-row">
              <div ref={wrapperRef} className="relative min-w-0 flex-1">
                <svg
                  viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
                  width="100%"
                  style={{ maxWidth: bounds.width, display: 'block', margin: '0 auto' }}
                  role="group"
                  aria-label="Hex map of this application's product areas and capabilities"
                  onMouseMove={handleSvgHover}
                  onMouseEnter={handleSvgHover}
                  onMouseLeave={() => {
                    setHover(null);
                    setHighlightAreaId(null);
                  }}
                >
                  {/* Capability tiles: flat, no stroke, packed edge-to-edge with a thin background gap. */}
                  {layout.tiles.map((tile) => {
                    const capability = capabilitiesById.get(tile.capabilityId);
                    if (!capability) return null;
                    const center = hexToPixel(tile.cell, HEX_RADIUS);
                    const fogged = isAreaFogged(tile.areaId);
                    const highlighted = highlightAreaId !== null && highlightAreaId === tile.areaId;
                    const base = fogged ? palette.neutral : highlighted ? palette.accent : palette.primary;

                    let fillOpacity = 1;
                    if (!fogged) {
                      if (capability.coverageStatus === 'uncovered') fillOpacity = COVERAGE_ALPHA.uncovered;
                      else if (capability.coverageStatus === 'stale') fillOpacity = COVERAGE_ALPHA.stale;
                      else if (capability.coverageStatus === 'known_issue') fillOpacity = COVERAGE_ALPHA.stale;
                    } else {
                      fillOpacity = COVERAGE_ALPHA.fogged;
                    }

                    return (
                      <g key={tile.capabilityId}>
                        <Link to="/product-map" search={{ capability: capability.id, propertyId, view: 'explore' }}>
                          <polygon
                            className="transition-colors duration-150"
                            data-hover-kind="tile"
                            data-hover-id={capability.id}
                            points={hexPoints(center, TILE_RADIUS)}
                            fill={base}
                            fillOpacity={fillOpacity}
                            stroke="none"
                          />
                        </Link>
                        {!fogged && capability.coverageStatus === 'stale' && (
                          <circle cx={center.x} cy={center.y} r={2.5} fill={palette.warning} style={{ pointerEvents: 'none' }} />
                        )}
                        {!fogged && capability.coverageStatus === 'known_issue' && (
                          <circle cx={center.x} cy={center.y} r={2.5} fill={palette.destructive} style={{ pointerEvents: 'none' }} />
                        )}
                        {capability.importance === 'p0' && (
                          <polygon
                            points={hexPoints(center, TILE_RADIUS - 4)}
                            fill="none"
                            stroke={palette.text}
                            strokeOpacity={0.5}
                            strokeWidth={1}
                            style={{ pointerEvents: 'none' }}
                          />
                        )}
                        {explorationOutcomeById.get(capability.id)?.outcome === 'unreached' && (
                          <g
                            transform={`translate(${center.x + 8}, ${center.y + 8})`}
                            style={{ pointerEvents: 'none' }}
                          >
                            <rect x={-4} y={-2} width={8} height={6} rx={1} fill={palette.destructive} />
                            <path
                              d="M -2 -2 v -2 a 2 2 0 0 1 4 0 v 2"
                              fill="none"
                              stroke={palette.destructive}
                              strokeWidth={1.2}
                            />
                          </g>
                        )}
                      </g>
                    );
                  })}

                  {/* Empty-area placeholders (Decision 6 v3): a real area with zero capabilities
                      still gets one layout cell so the landmass has no hole and the key row has
                      a hoverable/clickable target — drawn as a small grey empty tile. */}
                  {layout.areas.map((areaEntry) => {
                    if (areaEntry.areaId === null || (tileCountByArea.get(areaEntry.areaId) ?? 0) > 0) return null;
                    const cell = areaEntry.cells[0];
                    if (!cell) return null;
                    const center = hexToPixel(cell, HEX_RADIUS);
                    const highlighted = highlightAreaId !== null && highlightAreaId === areaEntry.areaId;
                    return (
                      <g key={`empty-${areaEntry.areaId}`}>
                        <Link to="/product-map" search={{ area: areaEntry.areaId, propertyId, view: 'explore' }}>
                          <polygon
                            className="transition-colors duration-150"
                            data-hover-kind="area"
                            data-hover-id={areaEntry.areaId}
                            points={hexPoints(center, TILE_RADIUS)}
                            fill={highlighted ? palette.accent : palette.neutral}
                            fillOpacity={COVERAGE_ALPHA.fogged}
                            stroke="none"
                          />
                        </Link>
                      </g>
                    );
                  })}

                  {/* Leads: dashed neutral "?" marker on the exterior cell placeLead finds, slowly pulsing. */}
                  {leadPlacements.map(({ lead, cell }) => {
                    const center = hexToPixel(cell, HEX_RADIUS);
                    return (
                      <g key={`lead-${lead.id}`} className="xmap-lead-pulse" data-hover-kind="lead" data-hover-id={lead.id}>
                        <circle
                          cx={center.x}
                          cy={center.y}
                          r={9}
                          fill="none"
                          stroke={palette.neutral}
                          strokeWidth={1.25}
                          strokeDasharray="3 3"
                        />
                        <text
                          x={center.x}
                          y={center.y}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill={palette.text}
                          fontSize={11}
                          fontWeight={600}
                        >
                          ?
                        </text>
                      </g>
                    );
                  })}

                  {/* Live workers: numbered dot with a pulsing outer ring, gliding between polls. */}
                  {workerPositions.map(({ worker, point }) => (
                    <g
                      key={`worker-${worker.runId}`}
                      transform={`translate(${point.x}, ${point.y})`}
                      style={{ transition: 'transform 0.6s ease' }}
                      data-hover-kind="worker"
                      data-hover-id={worker.runId}
                    >
                      <circle className="xmap-worker-pulse" cx={0} cy={0} r={9} fill={palette.text} fillOpacity={0.3} />
                      <circle cx={0} cy={0} r={9} fill={palette.text} stroke={palette.background} strokeWidth={2} />
                      <text
                        x={0}
                        y={0}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill={palette.background}
                        fontSize={10}
                        fontWeight={700}
                      >
                        {worker.index}
                      </text>
                    </g>
                  ))}
                </svg>

                {hover && (
                  <div
                    className="pointer-events-none absolute z-10 space-y-0.5 rounded-lg border p-3 shadow-lg"
                    style={{
                      left: hover.x,
                      top: hover.y,
                      width: HOVER_CARD_WIDTH,
                      backgroundColor: palette.background,
                      borderColor: palette.grid,
                    }}
                  >
                    {hover.kind === 'tile' && (
                      <>
                        <Text size="xs" className="font-mono text-muted-foreground">
                          {hover.capability.code ?? hover.capability.title}
                        </Text>
                        <Text size="sm" weight="medium">
                          {hover.capability.title}
                        </Text>
                        {hover.areaTitle && (
                          <Text size="xs" className="text-muted-foreground">
                            {hover.areaTitle}
                          </Text>
                        )}
                        <Text size="xs" className="text-muted-foreground">
                          {COVERAGE_STATUS_LABEL[hover.capability.coverageStatus]} · {hover.capability.importance.toUpperCase()}
                          {hover.age ? ` · ${hover.age}` : ''}
                        </Text>
                        {hover.capability.blockedReason && (
                          <Text size="xs" className="text-destructive">
                            {hover.capability.blockedReason}
                          </Text>
                        )}
                        <Text size="xs" className="italic text-muted-foreground">
                          Click to open in Product Map
                        </Text>
                      </>
                    )}
                    {hover.kind === 'lead' && (
                      <>
                        <Text size="sm" weight="medium">
                          {hover.lead.label}
                        </Text>
                        <Text size="xs" className="truncate font-mono text-muted-foreground">
                          {hover.lead.url}
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          Lead · open
                        </Text>
                      </>
                    )}
                    {hover.kind === 'worker' && (
                      <>
                        <Text size="sm" weight="medium">
                          Worker {hover.worker.index}
                        </Text>
                        {hover.worker.currentUrl && (
                          <Text size="xs" className="truncate font-mono text-muted-foreground">
                            {hover.worker.currentUrl}
                          </Text>
                        )}
                        {hover.areaTitle && (
                          <Text size="xs" className="text-muted-foreground">
                            {hover.areaTitle}
                          </Text>
                        )}
                      </>
                    )}
                    {hover.kind === 'area' && (
                      <>
                        <Text size="sm" weight="medium">
                          {hover.areaTitle}
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          No capabilities yet
                        </Text>
                      </>
                    )}
                  </div>
                )}
              </div>

              <ExplorationMapKey
                areas={keyRows}
                propertyId={propertyId}
                highlightAreaId={highlightAreaId}
                onHighlight={setHighlightAreaId}
              />
            </div>

            <ExplorationMapLegend palette={palette} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
